import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import withCustomComponent from 'ui/components/withCustomComponent';
import DefaultLayout from 'ui/layouts/DefaultLayout';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import { Pagination } from 'ui/components/listings/Pagination';
import { NoAccessNotice } from 'ui/components/NoAccessNotice';

import { BlogPageList } from 'ui/_pages/blog/BlogPageList';
import { BlogPageZeroCase } from 'ui/_pages/blog/BlogPageZeroCase';
import { BlogPostPageHeader } from 'ui/bw-ui/blog/BlogPostPageHeader';
import FeaturedBlogPostCarousel from 'ui/bw-ui/blog/FeaturedBlogPostCarousel';
import { useBlogPostsQuery } from 'ui/bw-ui/hooks/useBlogs';
import {
  parseIdParam,
  parsePageParam,
  parseSearchParam,
} from '@portal/env/utils/paginate';
import { useMobxStores } from '@portal/env/hooks/useMobxStores';

const PAGE_SIZE = 12;

function BlogPage({ componentName }) {
  const { t } = useTranslation();
  const router = useRouter();
  const { appStore, authStore } = useMobxStores();
  const { business, mainMenuDocked } = appStore;

  const page = parsePageParam(router.query.page);
  const categoryId = parseIdParam(router.query.categoryId);
  const query = parseSearchParam(router.query.query);
  const { data, isLoading, isPlaceholderData, error } = useBlogPostsQuery({
    page,
    size: PAGE_SIZE,
    categoryId,
    query,
  });
  const blogPosts = data?.BlogPosts ?? [];

  // Only paging gets a loading state: category changes are fast, and a spinner
  // during search overlaps the featured carousel. Remembers the filters of the
  // last settled results (guarded set during render).
  const filters = `${categoryId}|${query}`;
  const [settledFilters, setSettledFilters] = useState(filters);
  if (!isPlaceholderData && settledFilters !== filters) {
    setSettledFilters(filters);
  }
  const isUpdating = isPlaceholderData && settledFilters === filters;
  // The query waits for a token, so without one (and no user) nothing would render
  const hasNoAccess =
    !!error || (!authStore.accessToken && !authStore.isLoggedIn);

  return (
    <DefaultLayout
      componentName={componentName}
      title={t('Articles - {{Name}}', { Name: t(business.Name) })}
    >
      <main className="content content--custom" role="main">
        <div className={`container ${mainMenuDocked ? 'tw:lg:pr-8' : ''}`}>
          <BlogPostPageHeader
            categories={data?.Categories ?? []}
            posts={blogPosts}
          />
          {(isLoading || isUpdating) && <LoadingSpinner />}
          <NoAccessNotice
            error={
              hasNoAccess
                ? t("You currently don't have access to the articles list.")
                : null
            }
          />
          {data && (
            <>
              <FeaturedBlogPostCarousel />
              {/* While a new page loads, the previous results stay visible but
                  dimmed. The empty state is hidden during any change so it
                  can't describe the previous results. */}
              <div
                aria-busy={isUpdating}
                className={isUpdating ? 'tw:opacity-50' : undefined}
              >
                <BlogPageList blogPosts={blogPosts} />
                {!error && !isPlaceholderData && blogPosts.length === 0 && (
                  <BlogPageZeroCase />
                )}
                <Pagination
                  page={data.Page ?? page}
                  records={blogPosts}
                  top={data.Top ?? PAGE_SIZE}
                />
              </div>
            </>
          )}
        </div>
      </main>
    </DefaultLayout>
  );
}

export default withCustomComponent('BlogPage')(observer(BlogPage));
